/**
 * An agency's plan: what they are on, what it costs, and how to change it.
 *
 * Plan changes are admin-only by design — the app shows this read-only and tells
 * the agency to get in touch. This panel is therefore the only place a
 * subscription is ever edited.
 */
import { useState } from 'react';
import { CheckCircle, CurrencyInr, EnvelopeSimple } from '@phosphor-icons/react';
import type {
  Agency,
  BillingCycle,
  NextNotice,
  PaymentMethod,
  SubscriptionStatus,
} from '../lib/types';
import {
  usePlans,
  useRecordPayment,
  useUpdateSubscription,
} from '../queries/platform';
import { apiError } from '../lib/api';
import { formatDate, inr } from '../lib/format';
import { Badge, Field, Modal, SubmitButton, type Tone } from './ui';

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'On trial',
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

/** Resolved client-side purely for display; the API is the authority. */
function describe(agency: Agency): {
  status: SubscriptionStatus;
  detail: string;
  tone: Tone;
} {
  const sub = agency.subscription;
  if (!sub) return { status: 'expired', detail: 'No plan set', tone: 'bad' };

  const now = Date.now();
  const trialEnds = sub.trialEndsAt ? new Date(sub.trialEndsAt).getTime() : null;
  const periodEnds = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).getTime() : null;

  if (sub.status === 'trialing' && trialEnds && now < trialEnds) {
    const days = Math.ceil((trialEnds - now) / 86_400_000);
    return {
      status: 'trialing',
      detail: `${days} day${days === 1 ? '' : 's'} left · ends ${formatDate(sub.trialEndsAt)}`,
      tone: days <= 7 ? 'warn' : 'info',
    };
  }
  if (sub.status === 'active' && (!periodEnds || now < periodEnds)) {
    return {
      status: 'active',
      detail: `Renews ${formatDate(sub.currentPeriodEnd)}`,
      tone: 'ok',
    };
  }
  if (sub.status === 'cancelled') return { status: 'cancelled', detail: 'Cancelled', tone: 'off' };
  return {
    status: 'expired',
    detail: sub.trialEndsAt ? `Lapsed ${formatDate(sub.trialEndsAt)}` : 'Lapsed',
    tone: 'bad',
  };
}

/** Plain-English wording for each notice, for the "next invoice" line. */
const NOTICE_LABEL: Record<string, string> = {
  'trial-ending': 'Trial ending warning',
  'trial-ended': 'Invoice, trial ended',
  'renewal-due': 'Renewal reminder',
  'period-ended': 'Invoice, period ended',
  overdue: 'Overdue reminder',
};

const METHOD_LABEL: Record<PaymentMethod, string> = {
  upi: 'UPI',
  'bank-transfer': 'Bank transfer',
  cheque: 'Cheque',
  cash: 'Cash',
  card: 'Card',
  other: 'Other',
};

export default function SubscriptionPanel({
  agency,
  nextNotice,
}: {
  agency: Agency;
  nextNotice?: NextNotice | null;
}) {
  const { data: catalogue, isPending } = usePlans();
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [notice, setNotice] = useState('');

  const state = describe(agency);
  const sub = agency.subscription;
  const addOns = sub?.addOns ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <CurrencyInr size={17} color="var(--ac)" />
        <div>
          <h2 className="card-title">Subscription</h2>
          <p className="card-sub">Plan changes are made here, not by the agency</p>
        </div>
        <div className="spacer" />
        <Badge tone={state.tone}>{STATUS_LABEL[state.status]}</Badge>
      </div>

      <div className="card-pad stack" style={{ gap: 16 }}>
        {notice && (
          <p className="t-sm" style={{ color: 'var(--ok)', margin: 0 }}>
            {notice}
          </p>
        )}

        <div className="row" style={{ alignItems: 'flex-start', gap: 26, flexWrap: 'wrap' }}>
          <div>
            <p className="t-xs muted" style={{ margin: '0 0 4px' }}>
              {sub?.billingCycle === 'annual' ? 'Per year' : 'Per month'}
            </p>
            <p className="num" style={{ margin: 0, fontSize: 26, lineHeight: 1 }}>
              {inr(sub?.amount ?? 0)}
            </p>
          </div>
          <div>
            <p className="t-xs muted" style={{ margin: '0 0 4px' }}>
              Status
            </p>
            <p className="t-md strong" style={{ margin: 0 }}>
              {state.detail}
            </p>
          </div>
          <div>
            <p className="t-xs muted" style={{ margin: '0 0 4px' }}>
              Last payment
            </p>
            <p className="t-md" style={{ margin: 0 }}>
              {sub?.lastPaymentAt ? formatDate(sub.lastPaymentAt) : 'None yet'}
            </p>
            {sub?.lastPaymentNote && (
              <p className="t-xs faint" style={{ margin: '2px 0 0' }}>
                {sub.lastPaymentNote}
              </p>
            )}
          </div>
        </div>

        {/* When they next hear from us about money. */}
        <div
          className="row"
          style={{
            gap: 10,
            padding: '11px 13px',
            background: 'var(--inset)',
            borderRadius: 12,
            alignItems: 'flex-start',
          }}
        >
          <EnvelopeSimple size={16} color="var(--mute)" style={{ flex: 'none', marginTop: 1 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p className="t-sm strong" style={{ margin: 0 }}>
              {nextNotice
                ? `${NOTICE_LABEL[nextNotice.kind] ?? nextNotice.kind} on ${formatDate(nextNotice.on)}`
                : 'No further notices scheduled'}
            </p>
            <p className="t-xs muted" style={{ margin: '2px 0 0', lineHeight: 1.5 }}>
              {nextNotice
                ? `Emailed to ${agency.email ?? 'the agency'}. Payment due ${formatDate(nextNotice.dueOn)}.`
                : sub?.status === 'cancelled'
                  ? 'Cancelled accounts are not chased.'
                  : 'Nothing further is due until the plan changes.'}
            </p>
            {sub?.lastNoticeSentOn && (
              <p className="t-xs faint" style={{ margin: '4px 0 0' }}>
                Last sent: {NOTICE_LABEL[sub.lastNoticeKind ?? ''] ?? sub.lastNoticeKind} on{' '}
                {formatDate(sub.lastNoticeSentOn)}
              </p>
            )}
          </div>
        </div>

        <div>
          <p className="t-xs strong muted" style={{ margin: '0 0 8px', letterSpacing: 0.4 }}>
            ADD-ONS
          </p>
          {state.status === 'trialing' ? (
            <p className="t-sm muted" style={{ margin: 0, lineHeight: 1.6 }}>
              Every add-on is included free for the trial month. Nothing has been bought yet, so
              choose what they keep before the trial ends.
            </p>
          ) : addOns.length === 0 ? (
            <p className="t-sm muted" style={{ margin: 0 }}>
              None. Base plan only.
            </p>
          ) : (
            <div className="row" style={{ flexWrap: 'wrap', gap: 7 }}>
              {addOns.map((key) => (
                <Badge key={key} tone="ok">
                  <CheckCircle size={12} weight="fill" />
                  {catalogue?.addOns.find((a) => a.key === key)?.name ?? key}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {(sub?.payments?.length ?? 0) > 0 && (
          <div>
            <p className="t-xs strong muted" style={{ margin: '0 0 8px', letterSpacing: 0.4 }}>
              PAYMENTS
            </p>
            <div className="stack" style={{ gap: 6 }}>
              {[...(sub?.payments ?? [])]
                .reverse()
                .slice(0, 4)
                .map((payment, index) => (
                  <div
                    key={`${payment.paidOn}-${index}`}
                    className="row payment-row"
                    style={{
                      gap: 10,
                      padding: '9px 11px',
                      border: '1px solid var(--line-3)',
                      borderRadius: 10,
                    }}
                  >
                    <span className="num" style={{ fontSize: 13.5 }}>
                      {inr(payment.amount)}
                    </span>
                    <span className="t-sm muted">{METHOD_LABEL[payment.method] ?? payment.method}</span>
                    {payment.reference && (
                      <span className="t-xs faint truncate">{payment.reference}</span>
                    )}
                    <div className="spacer" />
                    <span className="t-xs muted">{formatDate(payment.paidOn)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="row" style={{ gap: 9 }}>
          <button className="btn" onClick={() => setEditing(true)} disabled={isPending}>
            Change plan
          </button>
          <button className="btn btn-ok" onClick={() => setPaying(true)}>
            Record payment
          </button>
        </div>
      </div>

      {editing && catalogue && (
        <EditPlanModal
          agency={agency}
          onClose={() => setEditing(false)}
          onDone={(message) => {
            setNotice(message);
            setEditing(false);
          }}
        />
      )}

      {paying && (
        <RecordPaymentModal
          agency={agency}
          onClose={() => setPaying(false)}
          onDone={(message) => {
            setNotice(message);
            setPaying(false);
          }}
        />
      )}
    </div>
  );
}

function EditPlanModal({
  agency,
  onClose,
  onDone,
}: {
  agency: Agency;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { data: catalogue } = usePlans();
  const update = useUpdateSubscription();
  const [cycle, setCycle] = useState<BillingCycle>(agency.subscription?.billingCycle ?? 'monthly');
  const [addOns, setAddOns] = useState<string[]>(agency.subscription?.addOns ?? []);
  const [status, setStatus] = useState<SubscriptionStatus>(
    agency.subscription?.status ?? 'trialing',
  );
  const [error, setError] = useState('');

  if (!catalogue) return null;

  const toggle = (key: string) =>
    setAddOns((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  // Mirrors priceFor() on the API. Shown live so the number is agreed before
  // the plan is saved, rather than discovered afterwards. Annual takes two
  // months off the add-ons as well as the base, so there is no multiplying by
  // twelve here — the yearly add-on price is its own figure.
  const annual = cycle === 'annual';
  const base = annual ? catalogue.pricing.annual : catalogue.pricing.monthly;
  const perAddOn = annual ? catalogue.pricing.addOnAnnual : catalogue.pricing.addOnMonthly;
  const total = base + addOns.length * perAddOn;

  const save = async () => {
    setError('');
    try {
      await update.mutateAsync({ id: agency._id, billingCycle: cycle, addOns, status });
      onDone('Plan updated.');
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <Modal
      title={`Plan for ${agency.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={update.isPending}>
            Cancel
          </button>
          <SubmitButton
            pending={update.isPending}
            pendingLabel="Saving…"
            className="btn btn-primary"
            onClick={save}
          >
            Save plan
          </SubmitButton>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field label="Billing cycle" hint="Annual is ten months' price for twelve, add-ons too.">
          <select
            className="select"
            value={cycle}
            onChange={(e) => setCycle(e.target.value as BillingCycle)}
          >
            <option value="monthly">Monthly, {inr(catalogue.pricing.monthly)}</option>
            <option value="annual">Annual, {inr(catalogue.pricing.annual)}</option>
          </select>
        </Field>

        <div>
          <label className="field-label">
            Add-ons at {inr(perAddOn)} per feature {annual ? 'per year' : 'per month'}
          </label>
          <div className="stack" style={{ gap: 7 }}>
            {catalogue.addOns.map((feature) => (
              <label
                key={feature.key}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '9px 11px',
                  border: `1px solid ${addOns.includes(feature.key) ? 'var(--ac-tint-2)' : 'var(--line-2)'}`,
                  background: addOns.includes(feature.key) ? 'var(--ac-tint)' : 'transparent',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={addOns.includes(feature.key)}
                  onChange={() => toggle(feature.key)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ minWidth: 0 }}>
                  <span className="t-md strong">{feature.name}</span>
                  <span className="t-xs muted" style={{ display: 'block', lineHeight: 1.5 }}>
                    {feature.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <Field
          label="Status"
          hint="Setting Active starts a paid period now. Expired closes the data screens."
        >
          <select
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}
          >
            <option value="trialing">Trialing</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>

        <div
          className="row"
          style={{ padding: '12px 14px', background: 'var(--inset)', borderRadius: 12 }}
        >
          <span className="t-md strong">Total</span>
          <div className="spacer" />
          <span className="num" style={{ fontSize: 19 }}>
            {inr(total)}
          </span>
          <span className="t-sm muted">/{cycle === 'annual' ? 'year' : 'month'}</span>
        </div>

        {error && <p className="field-error">{error}</p>}
      </div>
    </Modal>
  );
}

/**
 * Recording a payment that arrived by UPI, transfer or cheque.
 *
 * Every field here answers a question that gets asked later: how much actually
 * landed (a part payment is not a full one), *when* it landed (not when someone
 * got round to keying it in), how, and against which reference on the statement.
 */
function RecordPaymentModal({
  agency,
  onClose,
  onDone,
}: {
  agency: Agency;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const record = useRecordPayment();
  const expected = agency.subscription?.amount ?? 0;

  const [amount, setAmount] = useState(String(expected || ''));
  // Defaults to today, but back-dating is the common case — the transfer landed
  // on Friday and the console is opened on Monday.
  const [paidOn, setPaidOn] = useState(new Date().toISOString().split('T')[0]);
  const [method, setMethod] = useState<PaymentMethod>('upi');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const parsedAmount = Number(amount);
  const short = parsedAmount > 0 && expected > 0 && parsedAmount < expected;

  const submit = async () => {
    setError('');
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return setError('Enter the amount received');
    }
    if (!paidOn) return setError('Enter the date the payment was received');
    if (paidOn > new Date().toISOString().split('T')[0]) {
      return setError('The payment date cannot be in the future');
    }
    try {
      const response = await record.mutateAsync({
        id: agency._id,
        amount: parsedAmount,
        paidOn,
        method,
        reference: reference.trim() || undefined,
        note: note.trim() || undefined,
      });
      onDone(response.message);
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <Modal
      title="Record a payment"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={record.isPending}>
            Cancel
          </button>
          <SubmitButton
            pending={record.isPending}
            pendingLabel="Recording…"
            className="btn btn-primary"
            onClick={submit}
          >
            Record payment
          </SubmitButton>
        </>
      }
    >
      <p className="t-sm muted" style={{ margin: '0 0 14px', lineHeight: 1.6 }}>
        Marks {agency.name} active and moves the period on by one cycle. Paying early adds to the
        time they have left rather than discarding it.
      </p>

      <div className="stack" style={{ gap: 12 }}>
        <div className="grid grid-2" style={{ gap: 12 }}>
          <Field
            label="Amount received"
            hint={expected ? `Billed ${inr(expected)}` : undefined}
            required
          >
            <input
              className="input"
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1000"
            />
          </Field>
          <Field label="Date received" hint="When the money arrived." required>
            <input
              className="input"
              type="date"
              max={new Date().toISOString().split('T')[0]}
              value={paidOn}
              onChange={(e) => setPaidOn(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-2" style={{ gap: 12 }}>
          <Field label="Method">
            <select
              className="select"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            >
              <option value="upi">UPI</option>
              <option value="bank-transfer">Bank transfer</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field
            label="Transaction number"
            hint="UPI ref, UTR or cheque number."
          >
            <input
              className="input"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. 402931882145"
            />
          </Field>
        </div>

        <Field label="Note" hint="Anything worth remembering about this payment.">
          <textarea
            className="textarea"
            style={{ minHeight: 60 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Part payment, balance promised by Friday…"
          />
        </Field>

        {short && (
          <p className="t-sm" style={{ color: 'var(--warn)', margin: 0, lineHeight: 1.5 }}>
            That is {inr(expected - parsedAmount)} less than the {inr(expected)} billed. It will be
            recorded as a part payment, and the period still moves on a full cycle.
          </p>
        )}

        {error && <p className="field-error">{error}</p>}
      </div>
    </Modal>
  );
}
